// Columna derecha del cotizador, con las piezas del paso «Cotizar» del wizard: pago semanal,
// desglose del crédito, comparador de plazos y las acciones. Sin plan, la estructura queda vacía.
import type { ReactNode } from 'react';
import { ArrowRight, ClipboardList, Link2, Loader2 } from 'lucide-react';
import type { StoreQuoterPlan } from '@/api/services/loan-offerings/quoter.contract';
import { Button } from '@/kit';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import { NO_CELL_VALUE } from '@/shared/patterns/DataTable';
import { TierPill } from '@/shared/patterns/Pills';
import type { PromoSavings } from '@/domains/sales/wizard/promoSavings';
import { CreditBreakdown } from '@/domains/sales/wizard/components/CreditBreakdown';
import {
    WizardCard,
    WizardCardBody,
    WizardCardFoot,
    WizardCardHead,
} from '@/domains/sales/wizard/components/WizardCard';
import { EMPTY_TERM_SLOTS, NO_CREDIT_PLAN, NO_CREDIT_PLAN_HINT } from '../lib/quoterFormat';
import { formatRate, planTier } from '../lib/quoterPlans';
import type { QuoteNumbers, QuoteStatus, TermQuote } from '../useQuote';

const SHARE_UNAVAILABLE_REASON = 'Compartir la cotización llegará junto con el simulador.';

function money(cents: number): string {
    return formatMXN(cents / 100);
}

export function QuoteResult({
    priceCents,
    productName,
    plan,
    status,
    numbers,
    savings,
    terms,
    selectedTerm,
    onSelectTerm,
    onConvert,
}: {
    /** Precio de contado del equipo o monto libre; `0` = todavía no hay qué cotizar. */
    priceCents: number;
    productName: string | null;
    plan: StoreQuoterPlan | null;
    status: QuoteStatus;
    numbers: QuoteNumbers | null;
    savings: PromoSavings | null;
    terms: TermQuote[];
    selectedTerm: number | null;
    onSelectTerm: (termWeeks: number) => void;
    onConvert: () => void;
}) {
    const ready = status.kind === 'ready' && numbers !== null ? numbers : null;
    return (
        <WizardCard className="max-w-none lg:max-w-none">
            <WizardCardHead
                eyebrow="Crédito SureKeep"
                title="Cotización"
                right={plan ? <TierPill level={planTier(plan)} label="full" size="sm" /> : null}
                sub={
                    priceCents > 0 ? (
                        <>
                            {productName ? (
                                <>
                                    Equipo:{' '}
                                    <strong className="font-semibold text-foreground">
                                        {productName}
                                    </strong>{' '}
                                    ·{' '}
                                </>
                            ) : null}
                            Precio: <span className="tabular-nums">{money(priceCents)}</span>
                        </>
                    ) : undefined
                }
            />
            <WizardCardBody className="flex flex-col gap-5">
                {priceCents <= 0 ? (
                    <div className="flex flex-col items-center gap-2 py-10 text-center text-muted-foreground">
                        <ClipboardList className="size-8" aria-hidden />
                        <p className="text-[12.5px]">
                            Elige un producto del catálogo o escribe un monto libre para ver la
                            cuota semanal estimada.
                        </p>
                    </div>
                ) : ready ? (
                    <>
                        <QuoteHeadline n={ready} />
                        <CreditBreakdown
                            numbers={ready}
                            priceLabel="Precio del equipo"
                            savings={savings}
                            openingFee={
                                plan && ready.openingFee > 0
                                    ? { pct: plan.openingCommissionPct, amount: ready.openingFee }
                                    : null
                            }
                            creditCost={ready.creditCost}
                            ivaRate={plan?.ivaOnInterest}
                        />
                    </>
                ) : (
                    <StatusNotice status={status} />
                )}

                {priceCents > 0 && (
                    <>
                        <hr className="border-border" />
                        <section
                            aria-labelledby="quoter-terms-title"
                            className="flex flex-col gap-2.5"
                        >
                            <h2 id="quoter-terms-title" className="text-[13.5px] font-bold">
                                Compara los plazos
                            </h2>
                            <TermComparison
                                terms={terms}
                                selectedTerm={selectedTerm}
                                onSelect={onSelectTerm}
                            />
                        </section>
                    </>
                )}
            </WizardCardBody>
            <WizardCardFoot>
                <Button
                    size="sm"
                    variant="outline"
                    disabled
                    title={SHARE_UNAVAILABLE_REASON}
                    leftIcon={<Link2 className="size-3.5" aria-hidden />}
                >
                    Compartir cotización
                </Button>
                <Button
                    size="sm"
                    rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                    onClick={onConvert}
                >
                    Convertir a venta
                </Button>
            </WizardCardFoot>
        </WizardCard>
    );
}

/** Lo que el cliente pregunta primero: cuánto paga por semana, cuántas semanas y en total. */
function QuoteHeadline({ n }: { n: QuoteNumbers }) {
    return (
        <dl className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
            <div className="flex min-w-0 flex-col">
                <dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                    Pago semanal
                </dt>
                <dd
                    data-testid="quote-weekly"
                    className="text-[30px] font-bold leading-tight tabular-nums text-promo"
                >
                    {formatMXN(n.weekly)}
                </dd>
                <dd className="text-[12px] text-muted-foreground">
                    {`${String(n.weeks)} semanas · enganche ${String(Math.round((n.down / n.price) * 100))}% · TNA ${formatRate(n.tna)}`}
                </dd>
            </div>
            <div className="ml-auto flex min-w-0 flex-col items-end">
                <dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                    Total a pagar
                </dt>
                <dd data-testid="quote-total" className="text-[22px] font-bold tabular-nums">
                    {formatMXN(n.total)}
                </dd>
            </div>
        </dl>
    );
}

function StatusNotice({ status }: { status: QuoteStatus }) {
    if (status.kind === 'loading' || status.kind === 'ready') {
        return (
            <div className="flex flex-col items-center gap-2 py-10 text-center">
                <Loader2 className="size-6 animate-spin text-muted-foreground" aria-hidden />
                <p className="text-[12.5px] text-muted-foreground">Calculando la cotización…</p>
            </div>
        );
    }
    const { title, hint } =
        status.kind === 'no-plan'
            ? { title: NO_CREDIT_PLAN, hint: status.hint ?? NO_CREDIT_PLAN_HINT }
            : status.kind === 'no-feasible'
              ? { title: 'Sin cotización', hint: 'Elige otro nivel o un equipo de menor precio.' }
              : { title: 'Sin cotización', hint: 'Ajusta el enganche, el plazo o el nivel.' };
    return (
        <div data-testid="quote-status" className="flex flex-col gap-1 py-6 text-center">
            <p className="text-[13.5px] font-semibold">{title}</p>
            <p className="text-[12.5px] text-muted-foreground">{hint}</p>
        </div>
    );
}

function TermComparison({
    terms,
    selectedTerm,
    onSelect,
}: {
    terms: TermQuote[];
    selectedTerm: number | null;
    onSelect: (termWeeks: number) => void;
}) {
    return (
        <div className="overflow-hidden rounded-[10px] border border-border bg-card">
            <table className="w-full text-[12.5px] tabular-nums">
                <thead className="bg-muted/30">
                    <tr>
                        <Th>Plazo</Th>
                        <Th numeric>Pago semanal</Th>
                        <Th numeric>Total a pagar</Th>
                        <Th numeric>Costo del crédito</Th>
                    </tr>
                </thead>
                <tbody>
                    {terms.length === 0
                        ? Array.from({ length: EMPTY_TERM_SLOTS }, (_, index) => (
                              <tr key={index} className="text-muted-foreground">
                                  <Td>{`${NO_CELL_VALUE} semanas`}</Td>
                                  <Td numeric>{NO_CELL_VALUE}</Td>
                                  <Td numeric>{NO_CELL_VALUE}</Td>
                                  <Td numeric>{NO_CELL_VALUE}</Td>
                              </tr>
                          ))
                        : terms.map(({ termWeeks, simulation }) => {
                              const selected = termWeeks === selectedTerm;
                              return (
                                  <tr
                                      key={termWeeks}
                                      className={cn(selected && 'bg-muted/60 [&>td]:font-semibold')}
                                  >
                                      <Td>
                                          <button
                                              type="button"
                                              aria-pressed={selected}
                                              onClick={() => {
                                                  if (!selected) onSelect(termWeeks);
                                              }}
                                              className="underline-offset-2 hover:underline"
                                          >
                                              {`${String(termWeeks)} semanas`}
                                          </button>
                                      </Td>
                                      <Td numeric className={simulation ? 'text-promo' : undefined}>
                                          {simulation
                                              ? money(simulation.weeklyPaymentCents)
                                              : NO_CELL_VALUE}
                                      </Td>
                                      <Td numeric>
                                          {simulation
                                              ? money(simulation.totalCents)
                                              : NO_CELL_VALUE}
                                      </Td>
                                      <Td
                                          numeric
                                          className={simulation ? 'text-warning' : undefined}
                                      >
                                          {simulation
                                              ? money(simulation.creditCostCents)
                                              : NO_CELL_VALUE}
                                      </Td>
                                  </tr>
                              );
                          })}
                </tbody>
            </table>
        </div>
    );
}

function Th({ children, numeric }: { children: ReactNode; numeric?: boolean }) {
    return (
        <th
            scope="col"
            className={cn(
                'border-b border-border px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground',
                numeric ? 'text-right' : 'text-left',
            )}
        >
            {children}
        </th>
    );
}

function Td({
    children,
    numeric,
    className,
}: {
    children: ReactNode;
    numeric?: boolean;
    className?: string;
}) {
    return (
        <td
            className={cn(
                'border-b border-border/50 px-3 py-2 last:border-b-0',
                numeric && 'text-right',
                className,
            )}
        >
            {children}
        </td>
    );
}
